import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { EmptyState, StatusBadge, formatIDR } from "../Shared";
import { BatchForm } from "./BatchForm";
import { PayDialog } from "./PayDialog";
import { EnrollmentTable } from "./EnrollmentTable";
import { ForceDeleteDialog } from "./ForceDeleteDialog";
import { PROG } from "../../constants/testIds";
import { Plus, Pencil, Trash2, CalendarDays, Users, UserCheck, ChevronDown, AlertTriangle, Eraser } from "lucide-react";

export function BatchList({ programId, program }) {
  const { user } = useAuth();
  const { t } = useLang();
  const perms = user?.permissions || [];
  const canManage = perms.includes("batch.manage");
  const canViewEnrollments = perms.includes("enrollment.manage");
  const isMember = user?.role === "MEMBER";
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const [batches, setBatches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [openEnroll, setOpenEnroll] = useState(null);
  const [forceDelete, setForceDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/batches?program_id=${programId}`);
      setBatches(data.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("programs.confirmDeleteBatch"))) return;
    try {
      await api.delete(`/admin/batches/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (e) {
      const err = extractError(e);
      if (err.code === "BATCH_HAS_ENROLLMENTS" && isMasterAdmin) {
        setForceDelete(batches.find((b) => b.batch_id === id) || { batch_id: id });
        return;
      }
      toast.error(err.message);
    }
  };

  return (
    <div data-testid={PROG.batchList} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-[Outfit] text-lg font-bold text-white">{t("programs.batches")} · {program?.name}</h2>
        {canManage && (
          <Button data-testid={PROG.newBatchBtn} size="sm" onClick={() => setEditing({})} className="bg-white/10 hover:bg-white/15 text-white h-9">
            <Plus className="h-4 w-4 mr-1" /> {t("programs.newBatch")}
          </Button>
        )}
      </div>
      {editing && <BatchForm programId={programId} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {forceDelete && (
        <ForceDeleteDialog kind="batch" id={forceDelete.batch_id} name={`${forceDelete.code || ""} — ${forceDelete.name || ""}`}
          onClose={() => setForceDelete(null)} onDone={load} />
      )}
      {paying && <PayDialog batch={paying} onClose={() => { setPaying(null); load(); }} />}
      {batches.length === 0 ? (
        <EmptyState testId={PROG.batchEmpty} text={t("programs.noBatches")} />
      ) : (
        batches.map((b) => (
          <Card key={b.batch_id} data-testid={`batch-card-${b.batch_id}`} className="bg-[#161923] border-white/5">
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">{b.name}</h3>
                    <span className="text-xs font-mono text-slate-500">{b.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-slate-400 mt-1.5">
                    <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {b.start_date} → {b.end_date}</span>
                    <span data-testid={`batch-calendar-days-${b.batch_id}`} className="flex items-center gap-1 text-slate-300">
                      {b.calendar_days} {t("programs.calendarDays")}
                    </span>
                    {b.duration_matches_program === false && (
                      <span data-testid={`batch-duration-mismatch-${b.batch_id}`} className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-amber-300">
                        <AlertTriangle className="h-3 w-3" />
                        {t("programs.durationMismatch")}: {t("programs.durationDays")} {b.program_duration_days} · {t("programs.alignedEnd")} {b.aligned_end_date}
                      </span>
                    )}
                    <span className="flex items-center gap-1" data-testid={`batch-count-${b.batch_id}`}><Users className="h-3.5 w-3.5" /> {b.enrolled_count}/{b.capacity}</span>
                    <span className="text-[#F59E0B] font-semibold">{t("payments.baseFee")} {formatIDR(b.base_fee)} + {t("payments.uniqueCode").toLowerCase()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={b.status} />
                  {canManage && (
                    <>
                      <Button data-testid={`batch-edit-${b.batch_id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-300 hover:bg-white/5" onClick={() => setEditing(b)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button data-testid={`batch-delete-${b.batch_id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:bg-red-500/10" onClick={() => remove(b.batch_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      {isMasterAdmin && (
                        <Button data-testid={`batch-force-delete-${b.batch_id}`} size="sm" variant="ghost"
                          className="h-8 text-[#FF8A80] hover:bg-[#FF3B30]/10 text-xs" onClick={() => setForceDelete(b)}>
                          <Eraser className="h-3.5 w-3.5 mr-1" /> {t("del.manage")}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500 flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" /> Coach:</span>
                {b.coaches?.length === 0 && <span className="text-slate-600">{t("programs.noCoach")}</span>}
                {b.coaches?.map((c) => (
                  <span key={c.user_id} data-testid={`batch-coach-${b.batch_id}-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-2.5 py-0.5">
                    {c.full_name || c.email} <span className="text-emerald-500/70">· {c.members}</span>
                  </span>
                ))}
              </div>
              {canViewEnrollments && (
                <div className="space-y-3">
                  <button type="button" data-testid={`batch-enrollments-toggle-${b.batch_id}`} onClick={() => setOpenEnroll(openEnroll === b.batch_id ? null : b.batch_id)} className="text-xs text-slate-300 hover:text-white flex items-center gap-1">
                    <ChevronDown className={`h-3.5 w-3.5 transition ${openEnroll === b.batch_id ? "rotate-180" : ""}`} /> {t("enrollments.title")} ({b.seated_count})
                  </button>
                  {openEnroll === b.batch_id && <EnrollmentTable batchId={b.batch_id} />}
                </div>
              )}
              {isMember && ["OPEN", "RUNNING"].includes(b.status) && (
                <Button data-testid={`batch-pay-${b.batch_id}`} onClick={() => setPaying(b)} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-10">
                  {t("payments.payAndEnroll")}
                </Button>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
